/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Alert,
  SafeAreaView, StatusBar, Animated, Dimensions, ActivityIndicator, Modal,
  KeyboardAvoidingView, Platform, Clipboard, Share, ToastAndroid, Vibration,
  Switch, FlatList, RefreshControl, BackHandler,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// NOTE: Move this to the server. Backend proxy at /api/ai/generate-content
// is now the primary path; direct Gemini stays as a fallback.
const GEMINI_API_KEY = 'AIzaSyCrFiqpxwDs-ZOD0qfiiIxmFiP07J_dH88';
const BACKUP_MODELS  = ['gemini-2.5-flash-lite','gemini-2.0-flash','gemini-1.5-flash-latest','gemini-1.5-pro-latest'];

const { width, height } = Dimensions.get('window');

let MaterialCommunityIcons, FontAwesome5, Ionicons;
try {
  MaterialCommunityIcons = require('react-native-vector-icons/MaterialCommunityIcons').default;
  FontAwesome5 = require('react-native-vector-icons/FontAwesome5').default;
  Ionicons     = require('react-native-vector-icons/Ionicons').default;
} catch (error) {
  const FallbackIcon = ({ size, color }) => (
    <View style={{ width: size||24, height: size||24, backgroundColor: color||'#667eea', borderRadius: (size||24)/2 }} />
  );
  MaterialCommunityIcons = FontAwesome5 = Ionicons = FallbackIcon;
}

const STORAGE_KEYS = {
  USER_PROFILE:       (id) => `prof_${id}_userProfile`,
  AI_HISTORY:         (id) => `prof_${id}_aiHistory`,
  AI_FAVORITES:       (id) => `prof_${id}_aiFavorites`,
  AI_SETTINGS:        (id) => `prof_${id}_aiSettings`,
  AI_DRAFTS:          (id) => `prof_${id}_aiDrafts`,
  AI_ANALYTICS:       (id) => `prof_${id}_aiAnalytics`,
  API_USAGE:          (id) => `prof_${id}_aiApiUsage`,
  CUSTOM_TEMPLATES:   (id) => `prof_${id}_aiCustomTemplates`,
  TUTORIAL_COMPLETED: (id) => `prof_${id}_aiTutorial`,
};

const MAX_HISTORY = 100, MAX_FAVORITES = 50, MAX_DRAFTS = 20, MAX_CUSTOM_TEMPLATES = 30;

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIES + TEMPLATES + TONES (unchanged content, kept inline)
// ─────────────────────────────────────────────────────────────────────────────
const contentCategories = [
  { id:'resume',          title:'Resume',           icon:'file-document',   color:'#667eea', description:'Professional resume sections',  subcategories:['Summary','Experience','Skills','Education','Projects','Achievements'], pushTarget: 'summary' },
  { id:'cover_letter',    title:'Cover Letter',     icon:'email',           color:'#764ba2', description:'Personalized cover letters',     subcategories:['Full Letter','Opening','Body','Closing','Follow-up'], needsTargetJob: true },
  { id:'linkedin',        title:'LinkedIn',         icon:'linkedin',        color:'#0077b5', description:'LinkedIn posts & headlines',     subcategories:['Post','Headline','About','Experience','Recommendation'], pushTarget: 'headline' },
  { id:'interview',       title:'Interview Prep',   icon:'account-voice',   color:'#f093fb', description:'Interview answers',              subcategories:['Behavioral','Technical','Situational','STAR Method','Questions'], needsTargetJob: true },
  { id:'email',           title:'Professional Email', icon:'email-outline', color:'#4facfe', description:'Business emails',                subcategories:['Introduction','Follow-up','Thank You','Negotiation','Resignation'] },
  { id:'portfolio',       title:'Portfolio',        icon:'briefcase',       color:'#43e97b', description:'Project descriptions',           subcategories:['Overview','Technical Details','Results','Case Study'] },
  { id:'job_description', title:'Job Description',  icon:'clipboard-text',  color:'#fa709a', description:'Job descriptions',               subcategories:['Overview','Requirements','Responsibilities','Benefits'] },
  { id:'bio',             title:'Professional Bio', icon:'account-circle',  color:'#30cfd0', description:'Professional bios',              subcategories:['Short','Medium','Long','Social Media'], pushTarget: 'bio' },
];

const defaultTemplates = {
  resume: [
    { id:'res_1', title:'Software Developer', content:'Developed web apps using React.js and Node.js, improving engagement by 30%.', tags:['technical'] },
    { id:'res_2', title:'Project Manager',    content:'Led team of 8 to deliver $2M project ahead of schedule with 98% satisfaction.', tags:['leadership'] },
  ],
  cover_letter: [
    { id:'cl_1', title:'Opening - Experienced', content:'I am writing to express interest in [Position] at [Company]. With [X] years in [Field]...', tags:['experienced'] },
  ],
  linkedin: [
    { id:'li_1', title:'Thought Leadership', content:'3 key insights from my latest project on [Topic]: 1)... 2)... 3)... #innovation', tags:['engagement'] },
  ],
  interview: [
    { id:'int_1', title:'STAR - Leadership', content:'Situation: Team behind deadline. Task: Reorganize workflow. Action: Daily stand-ups. Result: On-time delivery.', tags:['star'] },
  ],
  email: [
    { id:'em_1', title:'Introduction', content:'Dear [Name], I was referred by [Referrer]. I am reaching out because [Reason].', tags:['networking'] },
  ],
  portfolio: [
    { id:'port_1', title:'E-Commerce Platform', content:'Full-stack platform serving 50K+ users. Stack: React, Node, PostgreSQL.', tags:['full-stack'] },
  ],
  job_description: [
    { id:'jd_1', title:'Senior Developer', content:'Seeking Senior Developer to architect solutions and mentor juniors.', tags:['hiring'] },
  ],
  bio: [
    { id:'bio_1', title:'Short Bio', content:'[Name] is a [Title] with [X] years in [Industry], specializing in [Skills].', tags:['short'] },
  ],
};

const toneOptions = [
  { id:'professional', label:'Professional', icon:'briefcase' },
  { id:'casual',       label:'Casual',       icon:'coffee' },
  { id:'formal',       label:'Formal',       icon:'tie' },
  { id:'enthusiastic', label:'Enthusiastic', icon:'star' },
  { id:'confident',    label:'Confident',    icon:'trophy' },
  { id:'humble',       label:'Humble',       icon:'hand-heart' },
  { id:'technical',    label:'Technical',    icon:'code-tags' },
  { id:'creative',     label:'Creative',     icon:'palette' },
  { id:'persuasive',   label:'Persuasive',   icon:'bullhorn' },
  { id:'diplomatic',   label:'Diplomatic',   icon:'handshake' },
];

const REFINE_ACTIONS = [
  { id:'shorter',       label:'Make Shorter',     icon:'arrow-collapse-vertical', instruction:'Rewrite this content to be 30% shorter while keeping all key points.' },
  { id:'longer',        label:'Make Longer',      icon:'arrow-expand-vertical',   instruction:'Expand this content with more detail, examples, and depth.' },
  { id:'formal',        label:'More Formal',      icon:'tie',                     instruction:'Rewrite this in a more formal, business-professional tone.' },
  { id:'casual',        label:'More Casual',      icon:'coffee',                  instruction:'Rewrite this in a more casual, conversational tone.' },
  { id:'grammar',       label:'Fix Grammar',      icon:'spellcheck',              instruction:'Fix any grammar, spelling, and punctuation issues. Keep meaning intact.' },
  { id:'simpler',       label:'Simpler Language', icon:'text-short',              instruction:'Rewrite using simpler, plainer language. Aim for clarity.' },
  { id:'bullet_points', label:'To Bullet Points', icon:'format-list-bulleted',    instruction:'Reformat this as a clear bullet-point list.' },
  { id:'narrative',     label:'To Paragraphs',    icon:'format-paragraph',        instruction:'Rewrite this as flowing paragraphs (no bullet points).' },
];

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE NORMALIZER — used to build rich prompt context
// ─────────────────────────────────────────────────────────────────────────────
const normalizeProfile = (raw) => {
  const p = raw || {};
  const skills = Array.isArray(p.skills) ? p.skills.map(s =>
    typeof s === 'string' ? { name: s, level: 'Intermediate' } : { name: s.name || s.skill_name || 'Skill', level: s.level || 'Intermediate' }
  ) : [];
  return {
    id:                p.id || p.user_id || null,
    userId:            p.user_id || p.id || null,
    firstName:         p.first_name || (p.name || '').split(' ')[0] || '',
    name:              p.name || p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim(),
    email:             p.email || '',
    phone:             p.phone || '',
    headline:          p.headline || p.title || '',
    jobTitle:          p.current_title || p.job_title || p.title || '',
    currentCompany:    p.current_company || '',
    industry:          p.industry || '',
    yearsOfExperience: Number(p.years_of_experience ?? p.experience_years ?? 0) || 0,
    location:          p.location || [p.city, p.country].filter(Boolean).join(', ') || '',
    summary:           p.summary || p.about || p.bio || '',
    linkedinUrl:       p.linkedin_url || p.linkedin || '',
    githubUrl:         p.github_url || '',
    portfolioUrl:      p.portfolio_url || '',
    resumeUrl:         p.resume_url || '',
    skills,
    experience:     Array.isArray(p.experience)     ? p.experience     : [],
    education:      Array.isArray(p.education)      ? p.education      : [],
    certifications: Array.isArray(p.certifications) ? p.certifications : [],
    languages:      Array.isArray(p.languages)      ? p.languages      : [],
    projects:       Array.isArray(p.projects)       ? p.projects       : [],
    awards:         Array.isArray(p.awards)         ? p.awards         : [],
    _raw: p,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// AI SERVICE — backend-proxied with Gemini fallback
// ─────────────────────────────────────────────────────────────────────────────
const callAI = async ({ prompt, maxTokens = 2048, temperature = 0.7, signal }) => {
  // Primary: server-proxied so the model key stays off-device
  try {
    const r = await apiFetch('/api/ai/generate-content', {
      method: 'POST',
      body: JSON.stringify({ prompt, max_tokens: maxTokens, temperature }),
      signal,
    });
    if (r && !r.error) {
      const text = r.text || r.content || r.response || r.output || r.result;
      if (typeof text === 'string' && text.trim()) {
        return { text: text.trim(), source: 'backend' };
      }
    }
  } catch (e) {
    console.warn('Backend AI call failed, falling back to Gemini direct:', e?.message);
  }

  // Fallback: direct Gemini
  for (const model of BACKUP_MODELS) {
    try {
      const controller = signal ? null : new AbortController();
      const tid = controller ? setTimeout(() => controller.abort(), 30000) : null;
      const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${GEMINI_API_KEY}`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: { temperature, topK: 1, topP: 1, maxOutputTokens: maxTokens },
        }),
        signal: signal || controller?.signal,
      });
      if (tid) clearTimeout(tid);
      const data = await response.json();
      if (!response.ok) { console.warn(`Model ${model} failed:`, data?.error?.message); continue; }
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
      if (text && text.trim()) return { text: text.trim(), source: model };
    } catch (e) {
      console.warn(`Model ${model} error:`, e?.message);
      continue;
    }
  }
  throw new Error('All AI providers unavailable. Check connection or try again.');
};

// ─────────────────────────────────────────────────────────────────────────────
// PROMPT BUILDER — rich context using full profile + target job
// ─────────────────────────────────────────────────────────────────────────────
const buildPrompt = ({ profile, category, subcategory, tone, wordCount, input, targetJob }) => {
  const lines = [];
  lines.push('You are an expert AI content assistant for a professional job seeker.');
  lines.push('Generate content that is specific, action-oriented, and tailored to the candidate\'s real profile.');
  lines.push('');

  // ── Candidate profile ──────────────────────────────────────────────
  lines.push('=== CANDIDATE PROFILE ===');
  lines.push(`Name: ${profile.name || 'Candidate'}`);
  if (profile.jobTitle)         lines.push(`Current title: ${profile.jobTitle}${profile.currentCompany ? ` at ${profile.currentCompany}` : ''}`);
  if (profile.industry)         lines.push(`Industry: ${profile.industry}`);
  if (profile.yearsOfExperience)lines.push(`Years of experience: ${profile.yearsOfExperience}`);
  if (profile.location)         lines.push(`Location: ${profile.location}`);
  if (profile.summary)          lines.push(`Summary: ${profile.summary.slice(0, 400)}`);

  if (profile.skills.length) {
    lines.push('');
    lines.push('Skills:');
    profile.skills.slice(0, 20).forEach(s => lines.push(`- ${s.name}${s.level ? ` (${s.level})` : ''}`));
  }

  if (profile.experience.length) {
    lines.push('');
    lines.push('Experience:');
    profile.experience.slice(0, 5).forEach(e => {
      const range = `${e.start_date || ''} → ${e.end_date || (e.current ? 'present' : '')}`;
      lines.push(`- ${e.title || e.position || 'Role'} at ${e.company || 'Company'} (${range})`);
      if (e.description) lines.push(`  ${String(e.description).slice(0, 200)}`);
    });
  }

  if (profile.education.length) {
    lines.push('');
    lines.push('Education:');
    profile.education.slice(0, 3).forEach(e => {
      lines.push(`- ${e.degree || ''} ${e.field_of_study || ''} – ${e.institution || ''}`.trim());
    });
  }

  if (profile.certifications.length) {
    lines.push('');
    lines.push('Certifications:');
    profile.certifications.slice(0, 5).forEach(c => lines.push(`- ${c.name || c.title}${c.issuing_organization ? ` (${c.issuing_organization})` : ''}`));
  }

  if (profile.projects.length) {
    lines.push('');
    lines.push('Notable projects:');
    profile.projects.slice(0, 5).forEach(p => {
      lines.push(`- ${p.name || p.title}: ${(p.description || '').slice(0, 150)}`);
    });
  }

  // ── Target job (optional) ──────────────────────────────────────────
  if (targetJob) {
    lines.push('');
    lines.push('=== TARGET JOB ===');
    lines.push(`Title: ${targetJob.title || targetJob.position || 'N/A'}`);
    lines.push(`Company: ${targetJob.company || targetJob.employer_name || 'N/A'}`);
    if (targetJob.location)        lines.push(`Location: ${targetJob.location}`);
    if (targetJob.job_type)        lines.push(`Type: ${targetJob.job_type}`);
    if (targetJob.salary)          lines.push(`Salary: ${targetJob.salary}`);
    if (targetJob.description)     lines.push(`Description: ${String(targetJob.description).slice(0, 800)}`);
    if (Array.isArray(targetJob.skills_required) && targetJob.skills_required.length) {
      lines.push(`Required skills: ${targetJob.skills_required.join(', ')}`);
    }
  }

  // ── The request ────────────────────────────────────────────────────
  const toneLabel = toneOptions.find(t => t.id === tone)?.label || 'Professional';
  const cat = contentCategories.find(c => c.id === category) || { title: 'Content' };
  lines.push('');
  lines.push('=== REQUEST ===');
  lines.push(`Content type: ${cat.title}`);
  lines.push(`Subcategory:  ${subcategory}`);
  lines.push(`Tone:         ${toneLabel}`);
  lines.push(`Target length: ~${wordCount} words`);
  lines.push(`User input:    "${input}"`);
  lines.push('');
  lines.push('=== INSTRUCTIONS ===');
  lines.push(`1. Generate a ${toneLabel.toLowerCase()} ${String(cat.title ?? '').toLowerCase()} focused on "${subcategory}".`);
  lines.push(`2. Reference SPECIFIC items from the candidate's profile where helpful — real roles, real projects, real skills.`);
  if (targetJob) lines.push(`3. Tailor explicitly to the target job above — mirror its language and address its requirements.`);
  lines.push(`${targetJob ? '4' : '3'}. Use measurable achievements where possible.`);
  lines.push(`${targetJob ? '5' : '4'}. Aim for approximately ${wordCount} words.`);
  lines.push('');
  lines.push('Output the content only — no preamble, no commentary.');

  return lines.join('\n');
};

const buildRefinePrompt = ({ instruction, content, profile }) => `${instruction}

Original content:
"""
${content}
"""

Keep the candidate context in mind:
- Name: ${profile.name || 'Candidate'}
- Title: ${profile.jobTitle || 'Professional'}
- ${profile.yearsOfExperience} years experience in ${profile.industry || 'their field'}

Return only the refined content. No commentary.`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const AIContentAssistantScreen = ({ navigation }) => {
  const profileCtx = useProfile();

  // ── State ────────────────────────────────────────────────────────
  const [userId, setUserId] = useState('default');
  const [profile, setProfile] = useState(normalizeProfile(null));

  const [selectedCategory, setSelectedCategory] = useState('resume');
  const [selectedSubcategory, setSelectedSubcategory] = useState('Summary');
  const [selectedTone, setSelectedTone] = useState('professional');
  const [wordCount, setWordCount] = useState(200);
  const [inputText, setInputText] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lastSource, setLastSource] = useState('');

  // Target job (cover letter / interview prep / resume tailoring)
  const [targetJob, setTargetJob] = useState(null);
  const [showJobPicker, setShowJobPicker] = useState(false);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  // History / favorites / drafts
  const [contentHistory, setContentHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [templates, setTemplates] = useState(defaultTemplates);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [analytics, setAnalytics] = useState({ totalGenerations:0, totalWordsGenerated:0, usageByCategory:{}, usageByDay:{}, averageResponseTime:0 });
  const [settings, setSettings] = useState({ autoSave:true, autoCopy:false, hapticFeedback:true, showTips:true, apiTimeout:30000, syncToCloud:true, includeFullProfile:true });
  const [apiUsage, setApiUsage] = useState({ count:0, lastReset:Date.now() });

  // UI state
  const [showHistory, setShowHistory] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showRefineMenu, setShowRefineMenu] = useState(false);
  const [showPushToProfile, setShowPushToProfile] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [filteredFavorites, setFilteredFavorites] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [exportFormat, setExportFormat] = useState('txt');
  const [isExporting, setIsExporting] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);

  const fadeAnim   = useRef(new Animated.Value(0)).current;
  const slideAnim  = useRef(new Animated.Value(50)).current;
  const scaleAnim  = useRef(new Animated.Value(1)).current;
  const isMounted  = useRef(true);

  // ─────────────────────────────────────────────────────────────────
  // BOOT
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    isMounted.current = true;
    (async () => {
      await initializeUser();
      animateIn();
    })();
    const sub = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
    return () => { isMounted.current = false; sub.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userId !== 'default') loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => { filterContent(); }, [searchQuery, contentHistory, favorites, templates, customTemplates]);
  useEffect(() => {
    const cat = contentCategories.find(c => c.id === selectedCategory);
    if (cat && cat.subcategories.length > 0) setSelectedSubcategory(cat.subcategories[0]);
    // Reset target job when switching to a category that doesn't need one
    if (cat && !cat.needsTargetJob) setTargetJob(null);
  }, [selectedCategory]);

  const initializeUser = async () => {
    try {
      await ProfileManagementService.bootstrap();
      const r = await ProfileManagementService.getProfile();
      if (r?.success && r?.data) {
        const np = normalizeProfile(r.data);
        if (isMounted.current) {
          setProfile(np);
          setUserId(String(np.userId || np.id || 'default'));
          await AsyncStorage.setItem(STORAGE_KEYS.USER_PROFILE(np.userId || np.id || 'default'), JSON.stringify(np));
        }
      } else {
        // Last-resort: keep old behaviour for offline cache
        const cached = await AsyncStorage.getItem(STORAGE_KEYS.USER_PROFILE('default'));
        if (cached) setProfile(normalizeProfile(JSON.parse(cached)));
      }
    } catch (e) {
      console.warn('initializeUser failed:', e?.message);
    }
  };

  const handleBackPress = () => {
    if (showHistory || showFavorites || showTemplates || showSettings || showAnalytics || showDrafts || showExport || showRefineMenu || showPushToProfile || showJobPicker) {
      setShowHistory(false); setShowFavorites(false); setShowTemplates(false); setShowSettings(false);
      setShowAnalytics(false); setShowDrafts(false); setShowExport(false); setShowRefineMenu(false);
      setShowPushToProfile(false); setShowJobPicker(false);
      return true;
    }
    return false;
  };

  // ─────────────────────────────────────────────────────────────────
  // DATA LOAD — DB + AsyncStorage hybrid (DB primary, local cache fallback)
  // ─────────────────────────────────────────────────────────────────
  const loadAllData = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadContentHistory(),
        loadFavorites(),
        loadCustomTemplates(),
        loadDrafts(),
        loadSettings(),
        loadAnalytics(),
        loadApiUsage(),
      ]);
    } catch (e) { console.warn('Error loading data:', e); }
    finally { if (isMounted.current) setIsRefreshing(false); }
  };

  const loadContentHistory = async () => {
    // Try DB first
    try {
      const r = await apiFetch(`/api/data/professional_ai_content?kind=history&sort=created_at&order=desc&limit=${MAX_HISTORY}`);
      const rows = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : null;
      if (rows && rows.length) {
        const items = rows.map(rowToHistoryItem);
        setContentHistory(items);
        await AsyncStorage.setItem(STORAGE_KEYS.AI_HISTORY(userId), JSON.stringify(items));
        return;
      }
    } catch {}
    // Local fallback
    try {
      const data = await AsyncStorage.getItem(STORAGE_KEYS.AI_HISTORY(userId));
      if (data) setContentHistory(JSON.parse(data));
    } catch (e) { console.warn('Local history load failed:', e?.message); }
  };

  const loadFavorites = async () => {
    try {
      const r = await apiFetch(`/api/data/professional_ai_content?kind=favorite&sort=created_at&order=desc&limit=${MAX_FAVORITES}`);
      const rows = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : null;
      if (rows && rows.length) {
        const items = rows.map(rowToHistoryItem);
        setFavorites(items);
        await AsyncStorage.setItem(STORAGE_KEYS.AI_FAVORITES(userId), JSON.stringify(items));
        return;
      }
    } catch {}
    try {
      const data = await AsyncStorage.getItem(STORAGE_KEYS.AI_FAVORITES(userId));
      if (data) setFavorites(JSON.parse(data));
    } catch (e) { console.warn('Local favorites load failed:', e?.message); }
  };

  const loadDrafts = async () => {
    try {
      const data = await AsyncStorage.getItem(STORAGE_KEYS.AI_DRAFTS(userId));
      if (data) setDrafts(JSON.parse(data));
    } catch {}
  };

  const loadCustomTemplates = async () => {
    try {
      const data = await AsyncStorage.getItem(STORAGE_KEYS.CUSTOM_TEMPLATES(userId));
      if (data) setCustomTemplates(JSON.parse(data));
    } catch {}
  };

  const loadSettings = async () => {
    try {
      const data = await AsyncStorage.getItem(STORAGE_KEYS.AI_SETTINGS(userId));
      if (data) setSettings(prev => ({ ...prev, ...JSON.parse(data) }));
    } catch {}
  };

  const loadAnalytics = async () => {
    try {
      const data = await AsyncStorage.getItem(STORAGE_KEYS.AI_ANALYTICS(userId));
      if (data) setAnalytics(JSON.parse(data));
    } catch {}
  };

  const loadApiUsage = async () => {
    try {
      const data = await AsyncStorage.getItem(STORAGE_KEYS.API_USAGE(userId));
      if (data) {
        const parsed = JSON.parse(data);
        if (Date.now() - parsed.lastReset > 86400000) setApiUsage({ count: 0, lastReset: Date.now() });
        else setApiUsage(parsed);
      }
    } catch {}
  };

  // Target jobs — pulled from candidate's applications + saved jobs
  const loadAvailableJobs = useCallback(async () => {
    setJobsLoading(true);
    const collected = [];
    try {
      // Applications (already-applied jobs)
      const r1 = await apiFetch(`/api/data/applications?candidate_email=${encodeURIComponent(profile.email)}&limit=30`).catch(() => null);
      const apps = Array.isArray(r1) ? r1 : Array.isArray(r1?.data) ? r1.data : [];
      for (const a of apps) {
        if (a.job_id) {
          try {
            const j = await apiFetch(`/api/data/jobs?id=${a.job_id}&limit=1`);
            const row = Array.isArray(j) ? j[0] : Array.isArray(j?.data) ? j.data[0] : j;
            if (row) collected.push({ ...row, _source: 'applied' });
          } catch {}
        }
      }
      // Saved jobs
      const r2 = await apiFetch(`/api/data/saved_jobs?limit=30`).catch(() => null);
      const saved = Array.isArray(r2) ? r2 : Array.isArray(r2?.data) ? r2.data : [];
      for (const s of saved) {
        if (s.job_id) {
          try {
            const j = await apiFetch(`/api/data/jobs?id=${s.job_id}&limit=1`);
            const row = Array.isArray(j) ? j[0] : Array.isArray(j?.data) ? j.data[0] : j;
            if (row) collected.push({ ...row, _source: 'saved' });
          } catch {}
        }
      }
      // Dedup by id
      const seen = new Set();
      const out = collected.filter(j => { const id = String(j.id); if (seen.has(id)) return false; seen.add(id); return true; });
      // Enrich with employer name via parallel company fetch
      const companyIds = [...new Set(out.map(j => j.company_id).filter(Boolean))];
      const compMap = {};
      await Promise.allSettled(
        companyIds.map(id =>
          apiFetch(`/api/data/company_profiles?id=${id}&limit=1`).then(res => {
            const row = Array.isArray(res) ? res[0] : Array.isArray(res?.data) ? res.data[0] : res;
            if (row) compMap[id] = row;
          }).catch(() => {})
        )
      );
      out.forEach(j => {
        if (j.company_id && compMap[j.company_id]) {
          j.company = compMap[j.company_id].company_name;
        }
      });
      if (isMounted.current) setAvailableJobs(out);
    } catch (e) {
      console.warn('Job pull for target picker failed:', e?.message);
    } finally {
      if (isMounted.current) setJobsLoading(false);
    }
  }, [profile.email]);

  const openTargetJobPicker = () => {
    setShowJobPicker(true);
    if (!availableJobs.length && profile.email) loadAvailableJobs();
  };

  // ─────────────────────────────────────────────────────────────────
  // DB ↔ history row mapping
  // ─────────────────────────────────────────────────────────────────
  const rowToHistoryItem = (r) => ({
    id:          String(r.id),
    serverId:    r.id,
    category:    r.category,
    subcategory: r.subcategory,
    tone:        r.tone,
    wordCount:   r.word_count,
    input:       r.input_text,
    output:      r.output_text,
    timestamp:   r.created_at || r.timestamp || new Date().toISOString(),
    favorite:    r.kind === 'favorite',
    tags:        Array.isArray(r.tags) ? r.tags : [],
    model:       r.model,
    responseTime: r.response_time_ms || 0,
    targetJobId: r.target_job_id || null,
  });

  const saveToHistoryDB = async (item, kind = 'history') => {
    const payload = {
      kind,
      category:    item.category,
      subcategory: item.subcategory,
      tone:        item.tone,
      word_count:  item.wordCount,
      input_text:  item.input,
      output_text: item.output,
      tags:        item.tags || [],
      model:       item.model,
      response_time_ms: item.responseTime,
      target_job_id:    item.targetJobId || null,
    };
    try {
      const r = await apiFetch('/api/data/professional_ai_content', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (r?.id) item.serverId = r.id;
    } catch (e) { console.warn('DB history save failed:', e?.message); }
  };

  const saveToHistory = async (output, metadata = {}) => {
    const newItem = {
      id:          Date.now().toString() + Math.random().toString(36).slice(2, 9),
      category:    selectedCategory,
      subcategory: selectedSubcategory,
      tone:        selectedTone,
      wordCount,
      input:       inputText,
      output,
      timestamp:   new Date().toISOString(),
      favorite:    false,
      tags:        metadata.tags || [],
      model:       metadata.source || '',
      responseTime: metadata.responseTime || 0,
      targetJobId: targetJob?.id || null,
    };
    const updated = [newItem, ...contentHistory.slice(0, MAX_HISTORY - 1)];
    setContentHistory(updated);
    await AsyncStorage.setItem(STORAGE_KEYS.AI_HISTORY(userId), JSON.stringify(updated));
    if (settings.syncToCloud) saveToHistoryDB(newItem, 'history');
    updateAnalytics(selectedCategory, output.length, metadata.responseTime || 0);
    return newItem;
  };

  // ─────────────────────────────────────────────────────────────────
  // GENERATE / REFINE
  // ─────────────────────────────────────────────────────────────────
  const generateContent = async () => {
    if (!inputText.trim()) { Alert.alert('Input Required', 'Please provide details first.'); return; }
    const cat = contentCategories.find(c => c.id === selectedCategory);
    if (cat?.needsTargetJob && !targetJob) {
      Alert.alert(
        'Pick a target job',
        `For best results on a ${String(cat.title ?? '').toLowerCase()}, choose the job you're targeting.`,
        [
          { text: 'Continue anyway', onPress: () => doGenerate() },
          { text: 'Pick job', onPress: openTargetJobPicker },
        ]
      );
      return;
    }
    doGenerate();
  };

  const doGenerate = async () => {
    const startTime = Date.now();
    setIsGenerating(true); setShowResult(false); setGeneratedContent(''); setLastSource('');
    try {
      const prompt = buildPrompt({
        profile: settings.includeFullProfile ? profile : { ...profile, experience: [], projects: [], certifications: [], education: [] },
        category: selectedCategory,
        subcategory: selectedSubcategory,
        tone: selectedTone,
        wordCount,
        input: inputText,
        targetJob,
      });
      const { text, source } = await callAI({ prompt, maxTokens: Math.min(wordCount * 4, 4096), temperature: 0.7 });
      const responseTime = Date.now() - startTime;
      setGeneratedContent(text);
      setLastSource(source);
      setShowResult(true);
      if (settings.autoCopy) await copyToClipboard(text, false);
      if (settings.hapticFeedback) Vibration.vibrate([0, 100, 50, 100]);
      await saveToHistory(text, { responseTime, source, tags: [selectedTone, selectedSubcategory] });
      const newUsage = { ...apiUsage, count: apiUsage.count + 1 };
      setApiUsage(newUsage);
      await AsyncStorage.setItem(STORAGE_KEYS.API_USAGE(userId), JSON.stringify(newUsage));
    } catch (error) {
      console.error('Generation error:', error);
      Alert.alert('Generation Failed', error?.message || 'Unknown error', [{ text: 'OK' }, { text: 'Retry', onPress: doGenerate }]);
    } finally {
      if (isMounted.current) setIsGenerating(false);
    }
  };

  const regenerateContent = async () => { await doGenerate(); };

  const refineContent = async (action) => {
    if (!generatedContent.trim()) return;
    setIsRefining(true);
    try {
      const prompt = buildRefinePrompt({ instruction: action.instruction, content: generatedContent, profile });
      const { text, source } = await callAI({ prompt, maxTokens: 4096, temperature: 0.6 });
      setGeneratedContent(text);
      setLastSource(source);
      if (settings.hapticFeedback) Vibration.vibrate(50);
      await saveToHistory(text, { source, tags: [selectedTone, selectedSubcategory, `refined:${action.id}`] });
    } catch (e) {
      Alert.alert('Refine Failed', e?.message || 'Try again.');
    } finally {
      setIsRefining(false);
      setShowRefineMenu(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // PUSH TO PROFILE
  // ─────────────────────────────────────────────────────────────────
  const pushToProfileField = async (field) => {
    if (!generatedContent.trim()) return;
    const labelMap = { summary: 'Profile Summary', headline: 'Headline', bio: 'Bio', about: 'About' };
    Alert.alert(
      `Save to ${labelMap[field] || field}?`,
      'This will replace the current value on your profile.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Save',
          onPress: async () => {
            try {
              const updates = {};
              if (field === 'summary')       updates.summary = generatedContent;
              if (field === 'about')         updates.about = generatedContent;
              if (field === 'headline')      updates.headline = generatedContent;
              if (field === 'bio')           updates.bio = generatedContent;
              if (field === 'experience_bullet' && profile.experience.length) {
                const updatedExp = [...profile.experience];
                const existing = updatedExp[0].description ? `${updatedExp[0].description}\n\n${generatedContent}` : generatedContent;
                updatedExp[0] = { ...updatedExp[0], description: existing };
                updates.experience = updatedExp;
              }
              await ProfileManagementService.updateProfile(updates);
              setProfile(p => ({ ...p, ...updates }));
              showToast('Saved to your profile');
              setShowPushToProfile(false);
            } catch (e) {
              Alert.alert('Save failed', e?.message || 'Could not update profile.');
            }
          },
        },
      ]
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // FAVORITES / DRAFTS / SETTINGS / ANALYTICS
  // ─────────────────────────────────────────────────────────────────
  const toggleFavorite = async (item) => {
    const isFav = favorites.some(f => f.id === item.id);
    let updatedFavorites, updatedHistory;
    if (isFav) {
      updatedFavorites = favorites.filter(f => f.id !== item.id);
      updatedHistory = contentHistory.map(h => h.id === item.id ? { ...h, favorite: false } : h);
      showToast('Removed from favorites');
    } else {
      const favItem = { ...item, favoritedAt: new Date().toISOString() };
      updatedFavorites = [favItem, ...favorites.slice(0, MAX_FAVORITES - 1)];
      updatedHistory   = contentHistory.map(h => h.id === item.id ? { ...h, favorite: true } : h);
      showToast('Added to favorites');
      if (settings.hapticFeedback) Vibration.vibrate(50);
      if (settings.syncToCloud) saveToHistoryDB(favItem, 'favorite');
    }
    setFavorites(updatedFavorites);
    setContentHistory(updatedHistory);
    await AsyncStorage.setItem(STORAGE_KEYS.AI_FAVORITES(userId), JSON.stringify(updatedFavorites));
    await AsyncStorage.setItem(STORAGE_KEYS.AI_HISTORY(userId), JSON.stringify(updatedHistory));
  };

  const saveDraft = async (text = inputText) => {
    if (!text.trim()) return;
    const newDraft = { id: Date.now().toString(), text, category: selectedCategory, subcategory: selectedSubcategory, tone: selectedTone, wordCount, savedAt: new Date().toISOString() };
    const updated = [newDraft, ...drafts.slice(0, MAX_DRAFTS - 1)];
    setDrafts(updated);
    await AsyncStorage.setItem(STORAGE_KEYS.AI_DRAFTS(userId), JSON.stringify(updated));
    setLastSaved(new Date());
    showToast('Draft saved');
  };

  const loadDraft = (draft) => {
    setInputText(draft.text);
    setSelectedCategory(draft.category);
    setSelectedSubcategory(draft.subcategory);
    setSelectedTone(draft.tone);
    setWordCount(draft.wordCount);
    setShowDrafts(false);
  };

  const deleteDraft = async (draftId) => {
    const updated = drafts.filter(d => d.id !== draftId);
    setDrafts(updated);
    await AsyncStorage.setItem(STORAGE_KEYS.AI_DRAFTS(userId), JSON.stringify(updated));
  };

  const deleteHistory = async (itemId) => {
    const updated = contentHistory.filter(h => h.id !== itemId);
    setContentHistory(updated);
    await AsyncStorage.setItem(STORAGE_KEYS.AI_HISTORY(userId), JSON.stringify(updated));
    // best-effort DB delete
    const item = contentHistory.find(h => h.id === itemId);
    if (item?.serverId) {
      apiFetch(`/api/data/professional_ai_content/${item.serverId}`, { method: 'DELETE' }).catch(() => {});
    }
  };

  const saveSettings = async (newSettings) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    await AsyncStorage.setItem(STORAGE_KEYS.AI_SETTINGS(userId), JSON.stringify(updated));
  };

  const updateAnalytics = (category, outputLength, responseTime) => {
    const today = new Date().toISOString().split('T')[0];
    const updated = {
      ...analytics,
      totalGenerations:    (analytics.totalGenerations || 0) + 1,
      totalWordsGenerated: (analytics.totalWordsGenerated || 0) + outputLength,
      usageByCategory:     { ...(analytics.usageByCategory || {}), [category]: ((analytics.usageByCategory || {})[category] || 0) + 1 },
      usageByDay:          { ...(analytics.usageByDay || {}),     [today]:    ((analytics.usageByDay || {})[today] || 0) + 1 },
      averageResponseTime: Math.round(((analytics.averageResponseTime || 0) + responseTime) / 2),
    };
    setAnalytics(updated);
    AsyncStorage.setItem(STORAGE_KEYS.AI_ANALYTICS(userId), JSON.stringify(updated)).catch(()=>{});
  };

  // ─────────────────────────────────────────────────────────────────
  // SEARCH FILTER
  // ─────────────────────────────────────────────────────────────────
  const filterContent = () => {
    const q = searchQuery.toLowerCase();
    const filter = (items, ks) => !q ? items : items.filter(i => ks.some(k => String(i[k] || '').toLowerCase().includes(q)));
    setFilteredHistory(filter(contentHistory, ['input', 'output', 'category', 'subcategory']));
    setFilteredFavorites(filter(favorites, ['input', 'output', 'category', 'subcategory']));
    const all = [];
    Object.keys(templates).forEach(cat => all.push(...templates[cat]));
    all.push(...customTemplates);
    setFilteredTemplates(filter(all, ['title', 'content']));
  };

  // ─────────────────────────────────────────────────────────────────
  // UTILS
  // ─────────────────────────────────────────────────────────────────
  const animateIn = () => {
    Animated.parallel([
      Animated.timing(fadeAnim,  { toValue: 1, duration: 600, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: 0, duration: 600, useNativeDriver: true }),
    ]).start();
  };
  const animateButtonPress = () => {
    Animated.sequence([
      Animated.timing(scaleAnim, { toValue: 0.96, duration: 100, useNativeDriver: true }),
      Animated.timing(scaleAnim, { toValue: 1,    duration: 100, useNativeDriver: true }),
    ]).start();
  };
  const copyToClipboard = async (text, showNotification = true) => {
    try {
      await Clipboard.setString(text);
      if (showNotification) { showToast('Copied!'); if (settings.hapticFeedback) Vibration.vibrate(50); }
    } catch { Alert.alert('Error', 'Failed to copy.'); }
  };
  const shareContent = async (text = generatedContent) => {
    try { await Share.share({ message: text, title: 'AI Generated Content' }); } catch {}
  };
  const showToast = (message) => {
    if (Platform.OS === 'android') ToastAndroid.show(message, ToastAndroid.SHORT);
    else Alert.alert('', message, [{ text: 'OK' }], { cancelable: true });
  };
  const formatDate = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
  };
  const getCategoryColor = (id) => contentCategories.find(c => c.id === id)?.color || '#667eea';
  const getCategoryTitle = (id) => contentCategories.find(c => c.id === id)?.title || 'Content';

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
        <Ionicons name="arrow-back" size={24} color="#fff" />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>AI Content Assistant</Text>
      <View style={styles.headerActions}>
        <TouchableOpacity style={styles.headerButton} onPress={() => setShowSearch(s => !s)}>
          <Ionicons name="search" size={22} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={styles.headerButton} onPress={() => setShowSettings(true)}>
          <Ionicons name="settings-outline" size={22} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderProfileBanner = () => {
    const complete = !!(profile.name && profile.jobTitle && profile.summary && profile.skills.length);
    return (
      <TouchableOpacity
        style={[styles.profileBanner, !complete && styles.profileBannerWarn]}
        onPress={() => navigation.navigate('Profile')}
        activeOpacity={0.85}
      >
        <View style={styles.profileBannerAvatar}>
          <Text style={styles.profileBannerInitial}>{(profile.name || 'U').charAt(0).toUpperCase()}</Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.profileBannerName} numberOfLines={1}>
            {profile.name || 'Set up your profile'}
          </Text>
          <Text style={styles.profileBannerSub} numberOfLines={1}>
            {complete
              ? `${profile.skills.length} skills · ${profile.yearsOfExperience}y exp · ${profile.experience.length} roles`
              : 'AI works best with a complete profile — tap to add'}
          </Text>
        </View>
        <View style={styles.profileBannerToggle}>
          <Text style={styles.profileBannerToggleLabel}>Full</Text>
          <Switch
            value={settings.includeFullProfile}
            onValueChange={v => saveSettings({ includeFullProfile: v })}
            trackColor={{ false:'#cbd5e1', true:'#667eea' }}
            thumbColor="#fff"
          />
        </View>
      </TouchableOpacity>
    );
  };

  const renderSearchBar = () => {
    if (!showSearch) return null;
    return (
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#94a3b8" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search history, favorites, templates..."
          placeholderTextColor="#94a3b8"
          value={searchQuery}
          onChangeText={setSearchQuery}
          autoFocus
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#94a3b8" />
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderCategoryCard = (category) => (
    <TouchableOpacity
      key={category.id}
      style={[styles.categoryCard, { backgroundColor: category.color }, selectedCategory === category.id && styles.selectedCategory]}
      onPress={() => { setSelectedCategory(category.id); if (settings.hapticFeedback) Vibration.vibrate(20); }}
      activeOpacity={0.8}
    >
      <MaterialCommunityIcons name={category.icon} size={28} color="#fff" style={styles.categoryIcon} />
      <Text style={styles.categoryTitle}>{category.title}</Text>
      <Text style={styles.categoryDescription} numberOfLines={2}>{category.description}</Text>
      {selectedCategory === category.id && (
        <View style={styles.selectedIndicator}>
          <Ionicons name="checkmark-circle" size={20} color="#fff" />
        </View>
      )}
    </TouchableOpacity>
  );

  const renderTargetJobChip = () => {
    const cat = contentCategories.find(c => c.id === selectedCategory);
    if (!cat?.needsTargetJob) return null;
    return (
      <TouchableOpacity style={styles.targetJobChip} onPress={openTargetJobPicker} activeOpacity={0.8}>
        <MaterialCommunityIcons name="target" size={18} color="#667eea" />
        <Text style={styles.targetJobChipText} numberOfLines={1}>
          {targetJob
            ? `Targeting: ${targetJob.title || targetJob.position} at ${targetJob.company || targetJob.employer_name || 'company'}`
            : 'Pick target job to tailor output →'}
        </Text>
        {targetJob && (
          <TouchableOpacity onPress={(e) => { e.stopPropagation?.(); setTargetJob(null); }}>
            <Ionicons name="close-circle" size={16} color="#667eea" />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    );
  };

  const renderSubcategorySelector = () => {
    const category = contentCategories.find(c => c.id === selectedCategory);
    if (!category) return null;
    return (
      <View style={styles.subcategoryContainer}>
        <Text style={styles.subcategoryLabel}>Focus Area</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {category.subcategories.map(sub => (
            <TouchableOpacity
              key={sub}
              style={[styles.subcategoryChip, selectedSubcategory === sub && styles.subcategoryChipActive]}
              onPress={() => setSelectedSubcategory(sub)}
            >
              <Text style={[styles.subcategoryChipText, selectedSubcategory === sub && styles.subcategoryChipTextActive]}>{sub}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderToneSelector = () => (
    <View style={styles.toneContainer}>
      <Text style={styles.toneLabel}>Tone</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {toneOptions.map(tone => (
          <TouchableOpacity
            key={tone.id}
            style={[
              styles.toneChip,
              selectedTone === tone.id && styles.toneChipActive,
              { borderColor: selectedTone === tone.id ? getCategoryColor(selectedCategory) : '#e2e8f0' },
            ]}
            onPress={() => setSelectedTone(tone.id)}
          >
            <MaterialCommunityIcons name={tone.icon} size={14} color={selectedTone === tone.id ? getCategoryColor(selectedCategory) : '#64748b'} />
            <Text style={[styles.toneChipText, selectedTone === tone.id && { color: getCategoryColor(selectedCategory) }]}>{tone.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderWordCountSelector = () => (
    <View style={styles.wordCountContainer}>
      <View style={styles.wordCountHeader}>
        <Text style={styles.wordCountLabel}>Length</Text>
        <Text style={styles.wordCountValue}>{wordCount} words</Text>
      </View>
      <View style={styles.wordCountButtons}>
        {[50, 100, 200, 300, 500, 800].map(count => (
          <TouchableOpacity
            key={count}
            style={[
              styles.wordCountButton,
              wordCount === count && [styles.wordCountButtonActive, { backgroundColor: getCategoryColor(selectedCategory) }],
            ]}
            onPress={() => setWordCount(count)}
          >
            <Text style={[styles.wordCountButtonText, wordCount === count && styles.wordCountButtonTextActive]}>{count}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderInputSection = () => (
    <View style={styles.inputSection}>
      <View style={styles.inputHeader}>
        <Text style={styles.inputLabel}>Describe What You Need</Text>
        <View style={styles.inputActions}>
          <TouchableOpacity onPress={() => setShowTemplates(true)} style={styles.inputActionButton}>
            <MaterialCommunityIcons name="file-document-multiple" size={18} color="#667eea" />
            <Text style={styles.inputActionText}>Templates</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setShowDrafts(true)} style={styles.inputActionButton}>
            <MaterialCommunityIcons name="content-save" size={18} color="#667eea" />
            <Text style={styles.inputActionText}>Drafts ({drafts.length})</Text>
          </TouchableOpacity>
        </View>
      </View>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.textInput}
          placeholder={`Describe the ${getCategoryTitle(selectedCategory).toLowerCase()} you need…`}
          placeholderTextColor="#94a3b8"
          value={inputText}
          onChangeText={setInputText}
          multiline
          numberOfLines={6}
          textAlignVertical="top"
          maxLength={2000}
        />
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', padding: 8, paddingTop: 0 }}>
          <TouchableOpacity onPress={() => saveDraft()}>
            <Text style={[styles.inputActionText, { marginLeft: 0 }]}>Save as draft</Text>
          </TouchableOpacity>
          <Text style={styles.charCount}>{inputText.length}/2000</Text>
        </View>
      </View>
      {lastSaved && <Text style={styles.autoSaveText}>Last saved {formatDate(lastSaved.toISOString())}</Text>}
    </View>
  );

  const renderGenerateButton = () => (
    <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
      <TouchableOpacity
        style={[styles.generateButton, isGenerating && styles.generatingButton, { backgroundColor: getCategoryColor(selectedCategory) }]}
        onPress={() => { animateButtonPress(); generateContent(); }}
        disabled={isGenerating}
        activeOpacity={0.8}
      >
        {isGenerating ? (
          <View style={styles.generatingContent}>
            <ActivityIndicator size={20} color="#fff" />
            <Text style={styles.generateButtonText}>Generating…</Text>
          </View>
        ) : (
          <>
            <MaterialCommunityIcons name="auto-fix" size={22} color="#fff" />
            <Text style={styles.generateButtonText}>Generate Content</Text>
            <MaterialCommunityIcons name="creation" size={18} color="#fff" style={{ marginLeft: 8 }} />
          </>
        )}
      </TouchableOpacity>
    </Animated.View>
  );

  const renderResultSection = () => {
    if (!showResult) return null;
    const cat = contentCategories.find(c => c.id === selectedCategory);
    const canPushToProfile = !!(cat?.pushTarget || selectedSubcategory === 'Summary' || selectedSubcategory === 'About');
    return (
      <Animated.View style={[styles.resultSection, { opacity: fadeAnim }]}>
        <View style={styles.resultHeader}>
          <View style={styles.resultTitleContainer}>
            <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
            <Text style={styles.resultTitle}>Generated Content</Text>
          </View>
          <View style={styles.resultActions}>
            <TouchableOpacity style={styles.resultActionButton} onPress={() => copyToClipboard(generatedContent)}>
              <MaterialCommunityIcons name="content-copy" size={18} color="#667eea" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.resultActionButton} onPress={() => shareContent(generatedContent)}>
              <MaterialCommunityIcons name="share-variant" size={18} color="#667eea" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.resultActionButton}
              onPress={() => toggleFavorite({
                id: Date.now().toString(), category: selectedCategory, subcategory: selectedSubcategory,
                tone: selectedTone, wordCount, input: inputText, output: generatedContent, timestamp: new Date().toISOString(),
              })}
            >
              <MaterialCommunityIcons name="heart-outline" size={18} color="#ef4444" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.resultActionButton} onPress={regenerateContent}>
              <MaterialCommunityIcons name="refresh" size={18} color="#667eea" />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.resultContainer}>
          <ScrollView style={styles.resultScroll} showsVerticalScrollIndicator={false}>
            <Text style={styles.resultText}>{generatedContent}</Text>
          </ScrollView>
          {isRefining && (
            <View style={styles.refiningOverlay}>
              <ActivityIndicator size={28} color="#667eea" />
              <Text style={[styles.resultMetaText, { marginTop: 8 }]}>Refining…</Text>
            </View>
          )}
        </View>

        <View style={styles.resultMeta}>
          <Text style={styles.resultMetaText}>
            {generatedContent.split(/\s+/).length} words · {selectedTone} · {selectedSubcategory}
            {lastSource ? ` · via ${lastSource}` : ''}
          </Text>
        </View>

        {/* Refinement actions strip */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.refineStrip}>
          {REFINE_ACTIONS.map(a => (
            <TouchableOpacity key={a.id} style={styles.refineBtn} onPress={() => refineContent(a)} disabled={isRefining}>
              <MaterialCommunityIcons name={a.icon} size={14} color="#475569" />
              <Text style={styles.refineBtnText}>{a.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {canPushToProfile && (
          <TouchableOpacity style={styles.pushToProfileBtn} onPress={() => setShowPushToProfile(true)}>
            <MaterialCommunityIcons name="account-arrow-right" size={18} color="#fff" />
            <Text style={styles.pushToProfileBtnText}>Push to My Profile</Text>
          </TouchableOpacity>
        )}
      </Animated.View>
    );
  };

  // ─── Modals ────────────────────────────────────────────────────────
  const renderTargetJobPicker = () => (
    <Modal visible={showJobPicker} animationType="slide" transparent onRequestClose={() => setShowJobPicker(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Pick Target Job</Text>
            <TouchableOpacity onPress={() => setShowJobPicker(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>

          {jobsLoading ? (
            <View style={{ padding: 40, alignItems: 'center' }}>
              <ActivityIndicator size={32} color="#667eea" />
              <Text style={[styles.resultMetaText, { marginTop: 12 }]}>Loading your jobs…</Text>
            </View>
          ) : availableJobs.length === 0 ? (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="briefcase-search-outline" size={64} color="#cbd5e1" />
              <Text style={styles.emptyStateText}>No jobs to target</Text>
              <Text style={styles.emptyStateSubtext}>Apply to or save jobs to use them here.</Text>
            </View>
          ) : (
            <FlatList
              data={availableJobs}
              keyExtractor={j => String(j.id)}
              contentContainerStyle={{ padding: 16 }}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.templateItem}
                  onPress={() => { setTargetJob(item); setShowJobPicker(false); }}
                >
                  <View style={styles.templateItemHeader}>
                    <Text style={styles.templateTitle}>{item.title || item.position}</Text>
                    <View style={styles.templateTag}>
                      <Text style={styles.templateTagText}>{item._source}</Text>
                    </View>
                  </View>
                  <Text style={styles.templateContent}>{item.company || item.employer_name || 'Unknown employer'}</Text>
                  {item.location ? <Text style={styles.templateUsage}>{item.location}</Text> : null}
                </TouchableOpacity>
              )}
            />
          )}
        </View>
      </View>
    </Modal>
  );

  const renderRefineMenu = () => (
    <Modal visible={showRefineMenu} animationType="slide" transparent onRequestClose={() => setShowRefineMenu(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Refine Content</Text>
            <TouchableOpacity onPress={() => setShowRefineMenu(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <View style={styles.templateList}>
            {REFINE_ACTIONS.map(a => (
              <TouchableOpacity key={a.id} style={styles.exportOption} onPress={() => refineContent(a)}>
                <MaterialCommunityIcons name={a.icon} size={22} color="#667eea" />
                <Text style={styles.exportOptionText}>{a.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderPushToProfileModal = () => (
    <Modal visible={showPushToProfile} animationType="slide" transparent onRequestClose={() => setShowPushToProfile(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Save to Profile</Text>
            <TouchableOpacity onPress={() => setShowPushToProfile(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <View style={styles.templateList}>
            <TouchableOpacity style={styles.exportOption} onPress={() => pushToProfileField('summary')}>
              <MaterialCommunityIcons name="text-account" size={22} color="#667eea" />
              <Text style={styles.exportOptionText}>Profile Summary</Text>
              <Text style={styles.exportOptionDesc}>Replaces summary</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.exportOption} onPress={() => pushToProfileField('headline')}>
              <MaterialCommunityIcons name="format-title" size={22} color="#667eea" />
              <Text style={styles.exportOptionText}>Headline</Text>
              <Text style={styles.exportOptionDesc}>Replaces headline</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.exportOption} onPress={() => pushToProfileField('about')}>
              <MaterialCommunityIcons name="account-circle" size={22} color="#667eea" />
              <Text style={styles.exportOptionText}>About</Text>
              <Text style={styles.exportOptionDesc}>Replaces about</Text>
            </TouchableOpacity>
            {profile.experience.length > 0 && (
              <TouchableOpacity style={styles.exportOption} onPress={() => pushToProfileField('experience_bullet')}>
                <MaterialCommunityIcons name="briefcase-plus-outline" size={22} color="#667eea" />
                <Text style={styles.exportOptionText}>Add to Latest Role</Text>
                <Text style={styles.exportOptionDesc}>Appends to "{profile.experience[0].title || profile.experience[0].position || 'Role'}"</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderHistoryModal = () => (
    <Modal visible={showHistory} animationType="slide" transparent onRequestClose={() => setShowHistory(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>History ({contentHistory.length})</Text>
            <View style={styles.modalHeaderActions}>
              <TouchableOpacity onPress={() => setShowExport(true)} style={{ marginRight: 16 }}>
                <MaterialCommunityIcons name="export" size={22} color="#667eea" />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowHistory(false)}>
                <Ionicons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>
          </View>
          <FlatList
            data={searchQuery ? filteredHistory : contentHistory}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <View style={styles.historyItem}>
                <View style={styles.historyItemContent}>
                  <View style={styles.historyHeader}>
                    <View style={[styles.historyCategoryBadge, { backgroundColor: getCategoryColor(item.category) + '20' }]}>
                      <Text style={[styles.historyCategory, { color: getCategoryColor(item.category) }]}>
                        {getCategoryTitle(item.category)}
                      </Text>
                    </View>
                    <Text style={styles.historyDate}>{formatDate(item.timestamp)}</Text>
                  </View>
                  <Text style={styles.historyInput} numberOfLines={2}>{item.input}</Text>
                  <Text style={styles.historyOutput} numberOfLines={3}>{item.output}</Text>
                  <View style={styles.historyMeta}>
                    <Text style={styles.historyMetaText}>{item.tone} · {item.subcategory}</Text>
                    <Text style={styles.historyMetaText}>{(item.output || '').split(/\s+/).length} words</Text>
                  </View>
                </View>
                <View style={styles.historyActions}>
                  <TouchableOpacity onPress={() => copyToClipboard(item.output)}>
                    <MaterialCommunityIcons name="content-copy" size={20} color="#667eea" />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => toggleFavorite(item)}>
                    <MaterialCommunityIcons name={item.favorite ? 'heart' : 'heart-outline'} size={20} color="#ef4444" />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => {
                    setSelectedCategory(item.category); setSelectedSubcategory(item.subcategory);
                    setSelectedTone(item.tone); setInputText(item.input);
                    setGeneratedContent(item.output); setShowResult(true); setShowHistory(false);
                  }}>
                    <MaterialCommunityIcons name="restore" size={20} color="#667eea" />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => deleteHistory(item.id)}>
                    <MaterialCommunityIcons name="delete-outline" size={20} color="#ef4444" />
                  </TouchableOpacity>
                </View>
              </View>
            )}
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="history" size={64} color="#cbd5e1" />
                <Text style={styles.emptyStateText}>No history yet</Text>
                <Text style={styles.emptyStateSubtext}>Generated content will appear here.</Text>
              </View>
            }
            refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={loadAllData} colors={['#667eea']} />}
          />
        </View>
      </View>
    </Modal>
  );

  const renderFavoritesModal = () => (
    <Modal visible={showFavorites} animationType="slide" transparent onRequestClose={() => setShowFavorites(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Favorites ({favorites.length})</Text>
            <TouchableOpacity onPress={() => setShowFavorites(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <FlatList
            data={searchQuery ? filteredFavorites : favorites}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <View style={styles.favoriteItem}>
                <View style={styles.favoriteItemContent}>
                  <View style={styles.favoriteHeader}>
                    <View style={[styles.favoriteCategoryBadge, { backgroundColor: getCategoryColor(item.category) + '20' }]}>
                      <Text style={[styles.favoriteCategory, { color: getCategoryColor(item.category) }]}>
                        {getCategoryTitle(item.category)}
                      </Text>
                    </View>
                    <Text style={styles.favoriteDate}>{formatDate(item.favoritedAt || item.timestamp)}</Text>
                  </View>
                  <Text style={styles.favoriteInput} numberOfLines={2}>{item.input}</Text>
                  <Text style={styles.favoriteOutput} numberOfLines={4}>{item.output}</Text>
                </View>
                <View style={styles.favoriteActions}>
                  <TouchableOpacity onPress={() => copyToClipboard(item.output)}>
                    <MaterialCommunityIcons name="content-copy" size={20} color="#667eea" />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => shareContent(item.output)}>
                    <MaterialCommunityIcons name="share-variant" size={20} color="#667eea" />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => toggleFavorite(item)}>
                    <MaterialCommunityIcons name="heart" size={20} color="#ef4444" />
                  </TouchableOpacity>
                </View>
              </View>
            )}
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="heart-outline" size={64} color="#cbd5e1" />
                <Text style={styles.emptyStateText}>No favorites yet</Text>
                <Text style={styles.emptyStateSubtext}>Tap the heart on any generation to save it.</Text>
              </View>
            }
          />
        </View>
      </View>
    </Modal>
  );

  const renderDraftsModal = () => (
    <Modal visible={showDrafts} animationType="slide" transparent onRequestClose={() => setShowDrafts(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Drafts ({drafts.length})</Text>
            <TouchableOpacity onPress={() => setShowDrafts(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <FlatList
            data={drafts}
            keyExtractor={d => d.id}
            renderItem={({ item }) => (
              <TouchableOpacity style={styles.draftItem} onPress={() => loadDraft(item)}>
                <View style={styles.draftHeader}>
                  <Text style={styles.draftCategory}>{getCategoryTitle(item.category)} · {item.subcategory}</Text>
                  <Text style={styles.draftDate}>{formatDate(item.savedAt)}</Text>
                </View>
                <Text style={styles.draftText} numberOfLines={3}>{item.text}</Text>
                <View style={styles.draftMeta}>
                  <Text style={styles.draftMetaText}>{item.tone} · ~{item.wordCount} words</Text>
                  <TouchableOpacity onPress={() => deleteDraft(item.id)}>
                    <MaterialCommunityIcons name="delete-outline" size={18} color="#ef4444" />
                  </TouchableOpacity>
                </View>
              </TouchableOpacity>
            )}
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="content-save-outline" size={64} color="#cbd5e1" />
                <Text style={styles.emptyStateText}>No drafts</Text>
              </View>
            }
          />
        </View>
      </View>
    </Modal>
  );

  const renderTemplatesModal = () => {
    const all = [];
    Object.keys(templates).forEach(cat => all.push(...templates[cat].map(t => ({ ...t, _cat: cat }))));
    customTemplates.forEach(t => all.push({ ...t, _cat: 'custom' }));
    const data = searchQuery ? filteredTemplates : all;
    return (
      <Modal visible={showTemplates} animationType="slide" transparent onRequestClose={() => setShowTemplates(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Templates</Text>
              <TouchableOpacity onPress={() => setShowTemplates(false)}>
                <Ionicons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={data}
              keyExtractor={(t, i) => t.id || `tpl_${i}`}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.templateItem}
                  onPress={() => { setInputText(item.content); setShowTemplates(false); }}
                >
                  <View style={styles.templateItemHeader}>
                    <Text style={styles.templateTitle}>{item.title}</Text>
                    <View style={styles.templateTags}>
                      {(item.tags || []).map((tag, i) => (
                        <View key={i} style={styles.templateTag}>
                          <Text style={styles.templateTagText}>{tag}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                  <Text style={styles.templateContent}>{item.content}</Text>
                </TouchableOpacity>
              )}
              contentContainerStyle={{ padding: 16 }}
              ListEmptyComponent={
                <View style={styles.emptyState}>
                  <MaterialCommunityIcons name="file-document-multiple-outline" size={64} color="#cbd5e1" />
                  <Text style={styles.emptyStateText}>No templates</Text>
                </View>
              }
            />
          </View>
        </View>
      </Modal>
    );
  };

  const renderSettingsModal = () => (
    <Modal visible={showSettings} animationType="slide" transparent onRequestClose={() => setShowSettings(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Settings</Text>
            <TouchableOpacity onPress={() => setShowSettings(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.settingsList}>
            <View style={styles.settingsSection}>
              <Text style={styles.settingsSectionTitle}>AI Behaviour</Text>
              <View style={styles.settingItem}>
                <View style={styles.settingInfo}>
                  <MaterialCommunityIcons name="account-details" size={20} color="#667eea" />
                  <Text style={styles.settingLabel}>Use full profile</Text>
                </View>
                <Switch value={settings.includeFullProfile} onValueChange={v => saveSettings({ includeFullProfile: v })}
                  trackColor={{ false:'#cbd5e1', true:'#667eea' }} thumbColor="#fff" />
              </View>
              <View style={styles.settingItem}>
                <View style={styles.settingInfo}>
                  <MaterialCommunityIcons name="cloud-sync" size={20} color="#667eea" />
                  <Text style={styles.settingLabel}>Sync history to cloud</Text>
                </View>
                <Switch value={settings.syncToCloud} onValueChange={v => saveSettings({ syncToCloud: v })}
                  trackColor={{ false:'#cbd5e1', true:'#667eea' }} thumbColor="#fff" />
              </View>
            </View>
            <View style={styles.settingsSection}>
              <Text style={styles.settingsSectionTitle}>Convenience</Text>
              <View style={styles.settingItem}>
                <View style={styles.settingInfo}>
                  <MaterialCommunityIcons name="clipboard-check" size={20} color="#667eea" />
                  <Text style={styles.settingLabel}>Auto-copy result</Text>
                </View>
                <Switch value={settings.autoCopy} onValueChange={v => saveSettings({ autoCopy: v })}
                  trackColor={{ false:'#cbd5e1', true:'#667eea' }} thumbColor="#fff" />
              </View>
              <View style={styles.settingItem}>
                <View style={styles.settingInfo}>
                  <MaterialCommunityIcons name="vibrate" size={20} color="#667eea" />
                  <Text style={styles.settingLabel}>Haptic feedback</Text>
                </View>
                <Switch value={settings.hapticFeedback} onValueChange={v => saveSettings({ hapticFeedback: v })}
                  trackColor={{ false:'#cbd5e1', true:'#667eea' }} thumbColor="#fff" />
              </View>
              <View style={styles.settingItem}>
                <View style={styles.settingInfo}>
                  <MaterialCommunityIcons name="lightbulb-on" size={20} color="#667eea" />
                  <Text style={styles.settingLabel}>Show tips</Text>
                </View>
                <Switch value={settings.showTips} onValueChange={v => saveSettings({ showTips: v })}
                  trackColor={{ false:'#cbd5e1', true:'#667eea' }} thumbColor="#fff" />
              </View>
            </View>
            <View style={styles.settingsSection}>
              <Text style={styles.settingsSectionTitle}>Data</Text>
              <TouchableOpacity style={styles.settingAction} onPress={() => setShowAnalytics(true)}>
                <MaterialCommunityIcons name="chart-line" size={20} color="#667eea" />
                <Text style={[styles.settingLabel, { flex: 1 }]}>View Analytics</Text>
                <Ionicons name="chevron-forward" size={18} color="#94a3b8" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.settingAction} onPress={() => setShowExport(true)}>
                <MaterialCommunityIcons name="export" size={20} color="#667eea" />
                <Text style={[styles.settingLabel, { flex: 1 }]}>Export History</Text>
                <Ionicons name="chevron-forward" size={18} color="#94a3b8" />
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.settingAction}
                onPress={() => Alert.alert('Clear all local data?', 'This removes locally cached history, favorites, drafts. Cloud copy is unaffected.', [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Clear', style: 'destructive', onPress: async () => {
                    setContentHistory([]); setFavorites([]); setDrafts([]); setAnalytics({});
                    await AsyncStorage.multiRemove([
                      STORAGE_KEYS.AI_HISTORY(userId), STORAGE_KEYS.AI_FAVORITES(userId),
                      STORAGE_KEYS.AI_DRAFTS(userId), STORAGE_KEYS.AI_ANALYTICS(userId),
                    ]);
                    showToast('Local data cleared');
                  }},
                ])}
              >
                <MaterialCommunityIcons name="trash-can-outline" size={20} color="#ef4444" />
                <Text style={[styles.settingLabel, { flex: 1, color: '#ef4444' }]}>Clear local data</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.settingsSection}>
              <Text style={styles.settingsSectionTitle}>About</Text>
              <View style={styles.aboutItem}>
                <Text style={styles.aboutLabel}>Today's generations</Text>
                <Text style={styles.aboutValue}>{apiUsage.count}</Text>
              </View>
              <View style={styles.aboutItem}>
                <Text style={styles.aboutLabel}>Profile used</Text>
                <Text style={styles.aboutValue}>{settings.includeFullProfile ? 'Full' : 'Basic'}</Text>
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderAnalyticsModal = () => {
    const total = analytics.totalGenerations || 0;
    const catKeys = Object.keys(analytics.usageByCategory || {});
    const maxCat = Math.max(1, ...catKeys.map(k => analytics.usageByCategory[k]));
    return (
      <Modal visible={showAnalytics} animationType="slide" transparent onRequestClose={() => setShowAnalytics(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Analytics</Text>
              <TouchableOpacity onPress={() => setShowAnalytics(false)}>
                <Ionicons name="close" size={24} color="#64748b" />
              </TouchableOpacity>
            </View>
            <ScrollView>
              <View style={styles.analyticsGrid}>
                <View style={[styles.analyticsCard, { backgroundColor: '#667eea15' }]}>
                  <Text style={[styles.analyticsNumber, { color: '#667eea' }]}>{total}</Text>
                  <Text style={styles.analyticsLabel}>Generations</Text>
                </View>
                <View style={[styles.analyticsCard, { backgroundColor: '#10b98115' }]}>
                  <Text style={[styles.analyticsNumber, { color: '#10b981' }]}>{analytics.totalWordsGenerated || 0}</Text>
                  <Text style={styles.analyticsLabel}>Words</Text>
                </View>
                <View style={[styles.analyticsCard, { backgroundColor: '#f59e0b15' }]}>
                  <Text style={[styles.analyticsNumber, { color: '#f59e0b' }]}>{favorites.length}</Text>
                  <Text style={styles.analyticsLabel}>Favorites</Text>
                </View>
                <View style={[styles.analyticsCard, { backgroundColor: '#ef444415' }]}>
                  <Text style={[styles.analyticsNumber, { color: '#ef4444' }]}>{(analytics.averageResponseTime || 0)}ms</Text>
                  <Text style={styles.analyticsLabel}>Avg response</Text>
                </View>
              </View>
              <View style={styles.analyticsSection}>
                <Text style={styles.analyticsSectionTitle}>By category</Text>
                {catKeys.map(k => (
                  <View key={k} style={styles.analyticsBar}>
                    <Text style={styles.analyticsBarLabel}>{getCategoryTitle(k)}</Text>
                    <View style={styles.analyticsBarTrack}>
                      <View style={[styles.analyticsBarFill, { width: `${(analytics.usageByCategory[k] / maxCat) * 100}%`, backgroundColor: getCategoryColor(k) }]} />
                    </View>
                    <Text style={styles.analyticsBarValue}>{analytics.usageByCategory[k]}</Text>
                  </View>
                ))}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  const renderExportModal = () => (
    <Modal visible={showExport} animationType="slide" transparent onRequestClose={() => setShowExport(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Export History</Text>
            <TouchableOpacity onPress={() => setShowExport(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <View style={styles.exportOptions}>
            {['txt', 'json', 'md', 'csv'].map(fmt => (
              <TouchableOpacity
                key={fmt}
                style={[styles.exportOption, exportFormat === fmt && styles.exportOptionActive]}
                onPress={() => setExportFormat(fmt)}
              >
                <MaterialCommunityIcons
                  name={{ txt: 'file-document', json: 'code-json', md: 'language-markdown', csv: 'file-delimited' }[fmt]}
                  size={22}
                  color={exportFormat === fmt ? '#667eea' : '#94a3b8'}
                />
                <Text style={[styles.exportOptionText, exportFormat === fmt && styles.exportOptionTextActive]}>{fmt.toUpperCase()}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity
            style={[styles.exportButton, isExporting && styles.exportButtonDisabled]}
            onPress={async () => {
              setIsExporting(true);
              try {
                let content = '';
                if (exportFormat === 'txt')  content = contentHistory.map(i => `=== ${getCategoryTitle(i.category)} (${formatDate(i.timestamp)}) ===\nInput: ${i.input}\nOutput: ${i.output}\n`).join('\n');
                if (exportFormat === 'json') content = JSON.stringify({ exportedAt: new Date().toISOString(), profile: { name: profile.name, email: profile.email }, items: contentHistory }, null, 2);
                if (exportFormat === 'md')   content = contentHistory.map(i => `## ${getCategoryTitle(i.category)} — ${formatDate(i.timestamp)}\n\n**Input:** ${i.input}\n\n**Output:**\n${i.output}\n\n---`).join('\n\n');
                if (exportFormat === 'csv')  content = 'Category,Subcategory,Tone,Input,Output,Timestamp\n' + contentHistory.map(i => `"${i.category}","${i.subcategory}","${i.tone}","${(i.input||'').replace(/"/g,'""')}","${(i.output||'').replace(/"/g,'""')}","${i.timestamp}"`).join('\n');
                await Share.share({ message: content, title: `AI Content Export — ${exportFormat.toUpperCase()}` });
              } catch (e) {
                Alert.alert('Export Failed', e?.message || 'Unknown error');
              } finally {
                setIsExporting(false);
                setShowExport(false);
              }
            }}
            disabled={isExporting}
          >
            <MaterialCommunityIcons name="export" size={20} color="#fff" />
            <Text style={styles.exportButtonText}>Export ({contentHistory.length} items)</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // ─────────────────────────────────────────────────────────────────
  // QUICK ACTIONS bar
  // ─────────────────────────────────────────────────────────────────
  const renderQuickActions = () => (
    <View style={styles.quickActionsContainer}>
      <Text style={styles.quickActionsTitle}>Quick Access</Text>
      <View style={styles.quickActionsGrid}>
        <TouchableOpacity style={styles.quickActionButton} onPress={() => setShowHistory(true)}>
          <View style={[styles.quickActionIcon, { backgroundColor: '#667eea15' }]}>
            <MaterialCommunityIcons name="history" size={22} color="#667eea" />
          </View>
          <Text style={styles.quickActionText}>History</Text>
          <Text style={styles.quickActionCount}>{contentHistory.length}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton} onPress={() => setShowFavorites(true)}>
          <View style={[styles.quickActionIcon, { backgroundColor: '#ef444415' }]}>
            <MaterialCommunityIcons name="heart" size={22} color="#ef4444" />
          </View>
          <Text style={styles.quickActionText}>Favorites</Text>
          <Text style={styles.quickActionCount}>{favorites.length}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton} onPress={() => setShowDrafts(true)}>
          <View style={[styles.quickActionIcon, { backgroundColor: '#f59e0b15' }]}>
            <MaterialCommunityIcons name="content-save" size={22} color="#f59e0b" />
          </View>
          <Text style={styles.quickActionText}>Drafts</Text>
          <Text style={styles.quickActionCount}>{drafts.length}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton} onPress={() => setShowAnalytics(true)}>
          <View style={[styles.quickActionIcon, { backgroundColor: '#10b98115' }]}>
            <MaterialCommunityIcons name="chart-line" size={22} color="#10b981" />
          </View>
          <Text style={styles.quickActionText}>Analytics</Text>
          <Text style={styles.quickActionCount}>{analytics.totalGenerations || 0}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderTipsSection = () => {
    if (!settings.showTips) return null;
    const tips = [
      { icon: 'account-details', color: '#667eea', text: 'Complete your profile — AI uses your skills, experience, projects, and certs to write better.' },
      { icon: 'target',          color: '#10b981', text: 'For cover letters & interview prep, pick a target job to tailor output to that company.' },
      { icon: 'auto-fix',        color: '#f59e0b', text: 'Use refinement actions to shorten, simplify, or formalize without regenerating.' },
      { icon: 'account-arrow-right', color: '#3b82f6', text: 'Like a generation? Push it directly to your profile summary, headline, or bio.' },
    ];
    return (
      <View style={styles.tipsSection}>
        <View style={styles.tipsHeader}>
          <Text style={styles.tipsTitle}>Pro Tips</Text>
          <TouchableOpacity onPress={() => saveSettings({ showTips: false })}>
            <Text style={styles.tipsDismiss}>Dismiss</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.tipsContainer}>
          {tips.map((tip, i) => (
            <View key={i} style={styles.tipItem}>
              <View style={[styles.tipIconContainer, { backgroundColor: tip.color + '20' }]}>
                <MaterialCommunityIcons name={tip.icon} size={18} color={tip.color} />
              </View>
              <Text style={styles.tipText}>{tip.text}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // ROOT RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      {renderHeader()}
      {renderSearchBar()}
      <KeyboardAvoidingView style={styles.content} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <Animated.ScrollView
          style={styles.scrollView}
          contentContainerStyle={{ paddingBottom: 40 }}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={loadAllData} colors={['#667eea']} />}
        >
          {renderProfileBanner()}

          <View style={[styles.section, { marginTop: 10 }]}>
            <Text style={styles.sectionTitle}>Content Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoriesContainer}>
              {contentCategories.map(renderCategoryCard)}
            </ScrollView>
          </View>

          {renderTargetJobChip()}
          {renderSubcategorySelector()}
          {renderToneSelector()}
          {renderWordCountSelector()}
          {renderInputSection()}
          {renderGenerateButton()}
          {renderResultSection()}
          {renderQuickActions()}
          {renderTipsSection()}

          <View style={styles.bottomPadding} />
        </Animated.ScrollView>
      </KeyboardAvoidingView>

      {renderHistoryModal()}
      {renderFavoritesModal()}
      {renderDraftsModal()}
      {renderTemplatesModal()}
      {renderSettingsModal()}
      {renderAnalyticsModal()}
      {renderExportModal()}
      {renderTargetJobPicker()}
      {renderRefineMenu()}
      {renderPushToProfileModal()}
    </SafeAreaView>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES (kept from original + new additions for target-job / refine / push)
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  header: { backgroundColor: '#667eea', flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, elevation: 4, shadowColor: '#000', shadowOffset: {width:0,height:2}, shadowOpacity: 0.1, shadowRadius: 4 },
  backButton: { padding: 8, marginRight: 8 },
  headerTitle: { flex: 1, fontSize: 20, fontWeight: '700', color: '#fff' },
  headerActions: { flexDirection: 'row', alignItems: 'center' },
  headerButton: { padding: 8, marginLeft: 8 },
  searchContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', marginHorizontal: 16, marginVertical: 8, paddingHorizontal: 12, borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0', elevation: 1 },
  searchIcon: { marginRight: 8 },
  searchInput: { flex: 1, paddingVertical: 10, fontSize: 15, color: '#1e293b' },
  content: { flex: 1 },
  scrollView: { flex: 1 },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: '#1e293b', marginBottom: 12, marginHorizontal: 16 },

  // Profile banner
  profileBanner: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#fff', marginHorizontal: 16, marginTop: 10, padding: 12,
    borderRadius: 14, borderWidth: 1, borderColor: '#e2e8f0',
  },
  profileBannerWarn: { borderColor: '#fbbf24', backgroundColor: '#fef3c7' },
  profileBannerAvatar: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#667eea', alignItems: 'center', justifyContent: 'center', marginRight: 10 },
  profileBannerInitial: { color: '#fff', fontSize: 16, fontWeight: '700' },
  profileBannerName: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  profileBannerSub:  { fontSize: 12, color: '#64748b', marginTop: 2 },
  profileBannerToggle: { alignItems: 'center' },
  profileBannerToggleLabel: { fontSize: 10, color: '#94a3b8', marginBottom: 2 },

  // Target job chip
  targetJobChip: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#667eea15', marginHorizontal: 16, marginBottom: 12,
    paddingHorizontal: 12, paddingVertical: 10, borderRadius: 10,
    borderWidth: 1, borderColor: '#667eea40',
  },
  targetJobChipText: { flex: 1, marginHorizontal: 8, fontSize: 13, color: '#667eea', fontWeight: '600' },

  // Categories
  categoriesContainer: { paddingHorizontal: 16 },
  categoryCard: { width: 150, padding: 16, borderRadius: 16, marginRight: 12, alignItems: 'center', elevation: 3, shadowColor: '#000', shadowOffset: {width:0,height:2}, shadowOpacity: 0.1, shadowRadius: 4 },
  selectedCategory: { transform: [{scale: 1.05}], elevation: 6, shadowOpacity: 0.2 },
  selectedIndicator: { position: 'absolute', top: 8, right: 8 },
  categoryIcon: { marginBottom: 10 },
  categoryTitle: { fontSize: 15, fontWeight: '700', color: '#fff', textAlign: 'center', marginBottom: 6 },
  categoryDescription: { fontSize: 12, color: 'rgba(255,255,255,0.85)', textAlign: 'center', lineHeight: 16 },

  // Sub / tone / word count
  subcategoryContainer: { marginHorizontal: 16, marginBottom: 16 },
  subcategoryLabel: { fontSize: 14, fontWeight: '600', color: '#475569', marginBottom: 8 },
  subcategoryChip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, backgroundColor: '#f1f5f9', marginRight: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  subcategoryChipActive: { backgroundColor: '#667eea', borderColor: '#667eea' },
  subcategoryChipText: { fontSize: 13, color: '#64748b', fontWeight: '500' },
  subcategoryChipTextActive: { color: '#fff' },
  toneContainer: { marginHorizontal: 16, marginBottom: 16 },
  toneLabel: { fontSize: 14, fontWeight: '600', color: '#475569', marginBottom: 8 },
  toneChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, backgroundColor: '#f1f5f9', marginRight: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  toneChipActive: { backgroundColor: '#667eea15' },
  toneChipText: { fontSize: 12, color: '#64748b', marginLeft: 4, fontWeight: '500' },
  wordCountContainer: { marginHorizontal: 16, marginBottom: 16 },
  wordCountHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  wordCountLabel: { fontSize: 14, fontWeight: '600', color: '#475569' },
  wordCountValue: { fontSize: 14, fontWeight: '700', color: '#667eea' },
  wordCountButtons: { flexDirection: 'row', flexWrap: 'wrap' },
  wordCountButton: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, backgroundColor: '#f1f5f9', borderWidth: 1, borderColor: '#e2e8f0', minWidth: 60, alignItems: 'center', marginRight: 8, marginBottom: 8 },
  wordCountButtonActive: { borderColor: 'transparent' },
  wordCountButtonText: { fontSize: 13, color: '#64748b', fontWeight: '600' },
  wordCountButtonTextActive: { color: '#fff' },

  // Input
  inputSection: { marginHorizontal: 16, marginBottom: 16 },
  inputHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  inputLabel: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  inputActions: { flexDirection: 'row' },
  inputActionButton: { flexDirection: 'row', alignItems: 'center', marginLeft: 12, padding: 4 },
  inputActionText: { fontSize: 12, color: '#667eea', marginLeft: 4, fontWeight: '600' },
  inputContainer: { backgroundColor: '#fff', borderRadius: 16, borderWidth: 1, borderColor: '#e2e8f0', elevation: 2, shadowColor: '#000', shadowOffset: {width:0,height:1}, shadowOpacity: 0.05, shadowRadius: 2 },
  textInput: { padding: 16, fontSize: 16, color: '#1e293b', minHeight: 140, lineHeight: 24 },
  charCount: { fontSize: 12, color: '#94a3b8' },
  autoSaveText: { fontSize: 11, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' },

  // Generate
  generateButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, paddingHorizontal: 24, borderRadius: 16, marginHorizontal: 16, marginBottom: 20, elevation: 4, shadowColor: '#000', shadowOffset: {width:0,height:4}, shadowOpacity: 0.15, shadowRadius: 8 },
  generatingButton: { opacity: 0.8 },
  generatingContent: { flexDirection: 'row', alignItems: 'center' },
  generateButtonText: { fontSize: 16, fontWeight: '700', color: '#fff', marginLeft: 8 },

  // Result
  resultSection: { marginHorizontal: 16, marginBottom: 20 },
  resultHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  resultTitleContainer: { flexDirection: 'row', alignItems: 'center' },
  resultTitle: { fontSize: 18, fontWeight: '700', color: '#1e293b', marginLeft: 8 },
  resultActions: { flexDirection: 'row' },
  resultActionButton: { padding: 8, marginLeft: 4, backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', elevation: 1 },
  resultContainer: { backgroundColor: '#fff', borderRadius: 16, borderWidth: 1, borderColor: '#e2e8f0', elevation: 2, maxHeight: 420, position: 'relative' },
  resultScroll: { padding: 16 },
  resultText: { fontSize: 15, lineHeight: 26, color: '#374151' },
  refiningOverlay: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(255,255,255,0.7)', alignItems: 'center', justifyContent: 'center', borderRadius: 16 },
  resultMeta: { marginTop: 8, paddingHorizontal: 4 },
  resultMetaText: { fontSize: 12, color: '#94a3b8' },

  refineStrip: { marginTop: 12 },
  refineBtn: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#f1f5f9', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16,
    marginRight: 8, borderWidth: 1, borderColor: '#e2e8f0',
  },
  refineBtnText: { fontSize: 12, color: '#475569', marginLeft: 4, fontWeight: '500' },

  pushToProfileBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#10b981', paddingVertical: 12, borderRadius: 12, marginTop: 12,
  },
  pushToProfileBtnText: { color: '#fff', fontSize: 14, fontWeight: '700', marginLeft: 8 },

  // Quick actions
  quickActionsContainer: { marginHorizontal: 16, marginBottom: 20 },
  quickActionsTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b', marginBottom: 12 },
  quickActionsGrid: { flexDirection: 'row', justifyContent: 'space-between' },
  quickActionButton: { alignItems: 'center', width: (width - 64) / 4 },
  quickActionIcon: { width: 48, height: 48, borderRadius: 14, alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  quickActionText: { fontSize: 12, fontWeight: '600', color: '#475569' },
  quickActionCount: { fontSize: 11, color: '#94a3b8', marginTop: 2 },

  // Tips
  tipsSection: { marginHorizontal: 16, marginBottom: 20 },
  tipsHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  tipsTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  tipsDismiss: { fontSize: 13, color: '#667eea', fontWeight: '600' },
  tipsContainer: { backgroundColor: '#fff', borderRadius: 16, padding: 16, borderWidth: 1, borderColor: '#e2e8f0', elevation: 1 },
  tipItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  tipIconContainer: { width: 32, height: 32, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  tipText: { fontSize: 14, color: '#475569', flex: 1, lineHeight: 20 },
  bottomPadding: { height: 40 },

  // Modals
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalContent: { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: height * 0.85, paddingBottom: 20 },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 20, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  modalTitle: { fontSize: 20, fontWeight: '700', color: '#1e293b' },
  modalHeaderActions: { flexDirection: 'row', alignItems: 'center' },
  templateList: { padding: 20 },
  templateItem: { backgroundColor: '#f8fafc', borderRadius: 14, padding: 16, marginBottom: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  templateItemHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  templateTitle: { fontSize: 15, fontWeight: '700', color: '#1e293b', flex: 1, marginRight: 8 },
  templateTags: { flexDirection: 'row', flexWrap: 'wrap' },
  templateTag: { backgroundColor: '#667eea20', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10, marginLeft: 4, marginBottom: 4 },
  templateTagText: { fontSize: 11, color: '#667eea', fontWeight: '600' },
  templateContent: { fontSize: 14, color: '#64748b', lineHeight: 20 },
  templateUsage: { fontSize: 12, color: '#94a3b8', marginTop: 8 },

  // History / favorites / drafts items
  historyItem: { backgroundColor: '#f8fafc', borderRadius: 14, marginHorizontal: 16, marginBottom: 12, borderWidth: 1, borderColor: '#e2e8f0', overflow: 'hidden' },
  historyItemContent: { padding: 16 },
  historyHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  historyCategoryBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  historyCategory: { fontSize: 12, fontWeight: '700' },
  historyDate: { fontSize: 12, color: '#94a3b8' },
  historyInput: { fontSize: 14, color: '#1e293b', fontWeight: '600', marginBottom: 6 },
  historyOutput: { fontSize: 13, color: '#64748b', lineHeight: 18 },
  historyMeta: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  historyMetaText: { fontSize: 11, color: '#94a3b8' },
  historyActions: { flexDirection: 'row', justifyContent: 'flex-end', padding: 12, borderTopWidth: 1, borderTopColor: '#f1f5f9' },
  favoriteItem: { backgroundColor: '#f8fafc', borderRadius: 14, marginHorizontal: 16, marginBottom: 12, borderWidth: 1, borderColor: '#e2e8f0', overflow: 'hidden' },
  favoriteItemContent: { padding: 16 },
  favoriteHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  favoriteCategoryBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  favoriteCategory: { fontSize: 12, fontWeight: '700' },
  favoriteDate: { fontSize: 12, color: '#94a3b8' },
  favoriteInput: { fontSize: 14, color: '#1e293b', fontWeight: '600', marginBottom: 6 },
  favoriteOutput: { fontSize: 13, color: '#64748b', lineHeight: 18 },
  favoriteActions: { flexDirection: 'row', justifyContent: 'flex-end', padding: 12, borderTopWidth: 1, borderTopColor: '#f1f5f9' },
  draftItem: { backgroundColor: '#f8fafc', borderRadius: 14, marginHorizontal: 16, marginBottom: 12, padding: 16, borderWidth: 1, borderColor: '#e2e8f0' },
  draftHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  draftCategory: { fontSize: 13, fontWeight: '700', color: '#667eea' },
  draftDate: { fontSize: 12, color: '#94a3b8' },
  draftText: { fontSize: 14, color: '#374151', lineHeight: 20, marginBottom: 8 },
  draftMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  draftMetaText: { fontSize: 12, color: '#94a3b8' },

  // Empty / settings / analytics / export
  emptyState: { alignItems: 'center', paddingVertical: 40 },
  emptyStateText: { fontSize: 16, fontWeight: '600', color: '#94a3b8', marginTop: 12 },
  emptyStateSubtext: { fontSize: 14, color: '#cbd5e1', marginTop: 4 },
  settingsList: { padding: 20 },
  settingsSection: { marginBottom: 24 },
  settingsSectionTitle: { fontSize: 13, fontWeight: '700', color: '#94a3b8', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 },
  settingItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  settingInfo: { flexDirection: 'row', alignItems: 'center' },
  settingLabel: { fontSize: 15, color: '#1e293b', marginLeft: 12 },
  settingAction: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  aboutItem: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  aboutLabel: { fontSize: 14, color: '#64748b' },
  aboutValue: { fontSize: 14, fontWeight: '600', color: '#1e293b' },
  analyticsGrid: { flexDirection: 'row', flexWrap: 'wrap', padding: 16, justifyContent: 'space-between' },
  analyticsCard: { width: (width - 56) / 2, padding: 16, borderRadius: 16, alignItems: 'center', marginBottom: 12 },
  analyticsNumber: { fontSize: 28, fontWeight: '800' },
  analyticsLabel: { fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: '600' },
  analyticsSection: { paddingHorizontal: 16, marginBottom: 20 },
  analyticsSectionTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b', marginBottom: 12 },
  analyticsBar: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  analyticsBarLabel: { width: 100, fontSize: 13, color: '#475569', fontWeight: '500' },
  analyticsBarTrack: { flex: 1, height: 8, backgroundColor: '#f1f5f9', borderRadius: 4, marginHorizontal: 8, overflow: 'hidden' },
  analyticsBarFill: { height: '100%', borderRadius: 4 },
  analyticsBarValue: { width: 30, fontSize: 13, fontWeight: '700', color: '#475569', textAlign: 'right' },
  exportOptions: { padding: 20 },
  exportOption: { flexDirection: 'row', alignItems: 'center', padding: 16, borderRadius: 14, backgroundColor: '#f8fafc', marginBottom: 12, borderWidth: 2, borderColor: '#f1f5f9' },
  exportOptionActive: { borderColor: '#667eea', backgroundColor: '#667eea10' },
  exportOptionText: { fontSize: 16, fontWeight: '700', color: '#475569', marginLeft: 12 },
  exportOptionTextActive: { color: '#667eea' },
  exportOptionDesc: { fontSize: 13, color: '#94a3b8', marginLeft: 'auto' },
  exportButton: { backgroundColor: '#667eea', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16, marginHorizontal: 20, marginBottom: 20, borderRadius: 16 },
  exportButtonDisabled: { opacity: 0.6 },
  exportButtonText: { fontSize: 16, fontWeight: '700', color: '#fff', marginLeft: 8 },
});

export default AIContentAssistantScreen;
